import { WasteManifestSource } from "generated-graphql/graphql";

import { SOURCE_LABEL, SOURCE_ORDER } from "../manifestSourceDisplay";
import { DiscrepancyTallyView } from "./discrepancyBreakdown";

/** Drives the encircled bullet icon + color in the Summary list. */
export type StatusLevel = "ok" | "warning" | "error" | "info" | "neutral";

export interface SummaryItem {
  key: string;
  /** Human-readable, sentence-style status (the whole line the user reads). */
  message: string;
  level: StatusLevel;
  /** Optional explanation shown via an info icon at the end of the line. */
  tooltip?: string;
}

/** Why RCRA site-level access matters — shown on the site-level-access line. */
const SITE_LEVEL_ACCESS_TOOLTIP =
  "Without RCRA site-level access, we can't pull RCRAInfo data for this facility " +
  "sooner than 90 days (from the public export), and can't guarantee a timely " +
  "response to EPA correction requests.";

export interface SummaryInput {
  availableSources: readonly WasteManifestSource[];
  isRegulatedEManifest: boolean | null | undefined;
  /** Open-discrepancy counts split by kind (compliance vs field). */
  discrepancyTally: DiscrepancyTallyView;
  daysSinceShipment: number | null | undefined;
  hasEManifest: boolean | null | undefined;
  /** Whether an exception report submission has been started/filed. */
  hasExceptionReport: boolean | null | undefined;
  exceptionReportFiled: boolean | null | undefined;
  /** Reported federal generator status; VSQG/non-generators aren't subject to upload rules. */
  reportedWasteGeneratorStatus: string | null | undefined;
  /** Whether the transporter/TSDF has been contacted (proof of contact recorded). */
  contacted: boolean | null | undefined;
}

/**
 * Generator statuses that are NOT subject to the 45/60-day e-Manifest upload
 * obligation (so no exception report can be required). Unknown/null status is
 * treated conservatively as potentially subject.
 */
const NOT_SUBJECT_TO_EMANIFEST_UPLOAD = new Set<string>([
  "VERY_SMALL_QUANTITY_GENERATOR",
  "NOT_A_GENERATOR",
]);

export function isSubjectToEManifestUpload(status: string | null | undefined): boolean {
  return !NOT_SUBJECT_TO_EMANIFEST_UPLOAD.has(status ?? "");
}

/** Number of days past shipment at which a 45-day contact obligation kicks in. */
const CONTACT_DEADLINE_DAYS = 45;
/** Number of days past shipment at which an exception report is required. */
const EXCEPTION_DEADLINE_DAYS = 60;

/** Human label for the generator statuses that aren't subject to upload rules. */
export const NOT_SUBJECT_LABEL: Record<string, string> = {
  VERY_SMALL_QUANTITY_GENERATOR: "a VSQG",
  NOT_A_GENERATOR: "not a hazardous waste generator",
};

const pluralize = (n: number, singular: string, plural: string) => (n === 1 ? singular : plural);

export function dataSourcesSummary(availableSources: readonly WasteManifestSource[]): SummaryItem {
  const present = SOURCE_ORDER.filter((s) => availableSources.includes(s));
  const presentLabels = present.map((s) => SOURCE_LABEL[s]).join(", ");

  if (present.length === 0) {
    return { key: "dataSources", message: "No data sources available", level: "error" };
  }
  if (present.length === SOURCE_ORDER.length) {
    return {
      key: "dataSources",
      message: `All data sources present: ${presentLabels}`,
      level: "ok",
    };
  }
  const missing = SOURCE_ORDER.filter((s) => !availableSources.includes(s)).map(
    (s) => SOURCE_LABEL[s]
  );
  // Missing e-Manifest (the source of truth) is the noteworthy gap.
  const eManifestMissing = !availableSources.includes(WasteManifestSource.EManifest);
  return {
    key: "dataSources",
    message: `Missing ${missing.join(", ")} data — have ${presentLabels}`,
    level: eManifestMissing ? "warning" : "info",
  };
}

export function siteLevelAccessSummary(
  isRegulatedEManifest: boolean | null | undefined
): SummaryItem {
  return isRegulatedEManifest
    ? {
        key: "siteLevelAccess",
        message: "RCRA site-level access granted",
        level: "ok",
        tooltip: SITE_LEVEL_ACCESS_TOOLTIP,
      }
    : {
        key: "siteLevelAccess",
        message: "Missing RCRA site-level access",
        level: "warning",
        tooltip: SITE_LEVEL_ACCESS_TOOLTIP,
      };
}

export function discrepanciesSummary(tally: DiscrepancyTallyView): SummaryItem {
  const { complianceTotal, fieldTotal, fieldUnresolved } = tally;
  const total = complianceTotal + fieldTotal;
  if (total === 0) {
    return { key: "discrepancies", message: "No discrepancies detected", level: "ok" };
  }
  // Break the total down by source of the flag (matches the list view's two
  // bubbles): EPA-flagged = compliance issues, Encamp-flagged = field
  // discrepancies. Not by unresolved/pending.
  const parts: string[] = [];
  if (complianceTotal > 0) parts.push(`${complianceTotal} EPA-flagged`);
  if (fieldTotal > 0) parts.push(`${fieldTotal} Encamp-flagged`);
  return {
    key: "discrepancies",
    message: `${total} ${pluralize(total, "discrepancy", "discrepancies")}: ${parts.join(", ")}`,
    // Pending-only field discrepancies read as info; anything needing action is warning.
    level: complianceTotal > 0 || fieldUnresolved > 0 ? "warning" : "info",
  };
}

/** The distinct e-Manifest upload compliance states. */
export type ComplianceLineKind =
  | "onTrack"
  | "notSubject"
  | "uploaded"
  | "contactNeeded"
  | "contacted"
  | "exceptionRequired"
  | "exceptionStarted"
  | "exceptionFiled";

export interface ComplianceLine {
  kind: ComplianceLineKind;
  /** One-line summary message + status level (drives the Summary list). */
  message: string;
  level: StatusLevel;
}

export interface ComplianceLinesInput {
  subjectToUpload: boolean;
  /** Label for a not-subject facility (e.g. "a VSQG"), when applicable. */
  notSubjectLabel?: string | null;
  hasEManifest: boolean;
  daysSinceShipment: number;
  /** Transporter/TSDF contacted (proof of contact recorded). */
  contacted: boolean;
  exceptionReportStatus: "none" | "started" | "filed";
}

/**
 * The e-Manifest upload compliance state, as an ordered list of lines — the
 * single source of truth shared by the Summary (one-liners) and the Compliance
 * section (rich bullets), so the two never disagree.
 *
 * Day-driven (the regulation is day-based): a contact obligation at 45 days, an
 * exception report at 60. Past 60 with no report, the exception is shown; an
 * outstanding contact is added only when it ALSO hasn't been made (both are then
 * due). A contact already made shows on its own only while still in the 45-day
 * window — once in exception territory the line no longer mentions the contact.
 */
export function getComplianceLines(input: ComplianceLinesInput): ComplianceLine[] {
  const { subjectToUpload, notSubjectLabel, hasEManifest, daysSinceShipment, contacted } = input;

  if (!subjectToUpload) {
    return [
      {
        kind: "notSubject",
        level: "ok",
        message: notSubjectLabel
          ? `Not subject to e-Manifest upload requirements — facility is ${notSubjectLabel}`
          : "Not subject to e-Manifest upload requirements",
      },
    ];
  }
  if (hasEManifest) {
    return [
      {
        kind: "uploaded",
        level: "ok",
        message: "e-Manifest uploaded — no exception-reporting obligation",
      },
    ];
  }
  if (input.exceptionReportStatus === "filed") {
    return [{ kind: "exceptionFiled", level: "info", message: "Exception report filed" }];
  }
  if (input.exceptionReportStatus === "started") {
    return [
      { kind: "exceptionStarted", level: "warning", message: "Exception report in progress" },
    ];
  }

  const days = daysSinceShipment;
  const contactNeeded: ComplianceLine = {
    kind: "contactNeeded",
    level: "warning",
    message: `TSDF contact overdue — ${days} days since shipment, no e-Manifest`,
  };

  if (days >= EXCEPTION_DEADLINE_DAYS) {
    const lines: ComplianceLine[] = [
      {
        kind: "exceptionRequired",
        level: "error",
        message: `Exception report required — ${days} days since shipment, no e-Manifest`,
      },
    ];
    // Both actions are due only when the contact also hasn't been made.
    if (!contacted) lines.push(contactNeeded);
    return lines;
  }
  if (days >= CONTACT_DEADLINE_DAYS) {
    return contacted
      ? [{ kind: "contacted", level: "ok", message: "TSDF contacted" }]
      : [contactNeeded];
  }
  return [
    {
      kind: "onTrack",
      level: "ok",
      message: `On track — ${days} days since shipment, no e-Manifest yet`,
    },
  ];
}

function exceptionReportStatusOf(input: SummaryInput): "none" | "started" | "filed" {
  if (input.exceptionReportFiled) return "filed";
  if (input.hasExceptionReport) return "started";
  return "none";
}

/** Build the shared {@link ComplianceLinesInput} from a SummaryInput. */
export function complianceLinesInputFromSummary(input: SummaryInput): ComplianceLinesInput {
  return {
    subjectToUpload: isSubjectToEManifestUpload(input.reportedWasteGeneratorStatus),
    notSubjectLabel: NOT_SUBJECT_LABEL[input.reportedWasteGeneratorStatus ?? ""],
    hasEManifest: !!input.hasEManifest,
    daysSinceShipment: input.daysSinceShipment ?? 0,
    contacted: !!input.contacted,
    exceptionReportStatus: exceptionReportStatusOf(input),
  };
}

export function buildSummaryItems(input: SummaryInput): SummaryItem[] {
  // Compliance status (possibly several lines) sits right below site-level
  // access; discrepancies last.
  const complianceItems: SummaryItem[] = getComplianceLines(
    complianceLinesInputFromSummary(input)
  ).map((line) => ({ key: `compliance-${line.kind}`, message: line.message, level: line.level }));

  return [
    dataSourcesSummary(input.availableSources),
    siteLevelAccessSummary(input.isRegulatedEManifest),
    ...complianceItems,
    discrepanciesSummary(input.discrepancyTally),
  ];
}

export interface TimelineEvent {
  key: string;
  label: string;
  /** Raw ISO date string, or null when the event hasn't happened yet. */
  date: string | null | undefined;
  done: boolean;
  /** "warning" (orange) for compliance-path events; defaults to the standard green flow. */
  tone?: "default" | "warning";
}

export interface TimelineInput {
  shippedDate: string | null | undefined;
  receivedDate: string | null | undefined;
  eManifestCreatedDate: string | null | undefined;
  eManifestCertifiedDate: string | null | undefined;
  /** When the transporter/vendor was contacted (the 45-day proof of contact). */
  transporterContactedAt?: string | null;
  /** When an exception report was filed — set only once it's filed. */
  exceptionFiledDate?: string | null;
}

/**
 * The manifest lifecycle as a chronological flow. Events that have happened are
 * ordered by their actual date (so the flow always reads left-to-right in time,
 * even when, say, the e-Manifest was created before the recorded ship date);
 * not-yet-happened events keep their canonical lifecycle order and trail the
 * dated ones.
 */
export function buildTimelineEvents(input: TimelineInput): TimelineEvent[] {
  const canonical: TimelineEvent[] = [
    { key: "shipped", label: "Shipped", date: input.shippedDate, done: input.shippedDate != null },
    {
      key: "eManifestCreated",
      label: "e-Manifest created",
      date: input.eManifestCreatedDate,
      done: input.eManifestCreatedDate != null,
    },
    {
      key: "eManifestCertified",
      label: "e-Manifest certified",
      date: input.eManifestCertifiedDate,
      done: input.eManifestCertifiedDate != null,
    },
    {
      key: "received",
      label: "Received by TSDF",
      date: input.receivedDate,
      done: input.receivedDate != null,
    },
  ];

  // Compliance-path events — only present once they've happened, so always
  // dated; rendered in the warning (orange) tone to set them apart from the
  // standard green lifecycle flow.
  const compliance: TimelineEvent[] = [];
  if (input.transporterContactedAt != null) {
    compliance.push({
      key: "vendorContact",
      label: "TSDF Contacted",
      date: input.transporterContactedAt,
      done: true,
      tone: "warning",
    });
  }
  if (input.exceptionFiledDate != null) {
    compliance.push({
      key: "exceptionFiled",
      label: "Exception Filed",
      date: input.exceptionFiledDate,
      done: true,
      tone: "warning",
    });
  }

  const all = [...canonical, ...compliance];
  const dated = all
    .filter((e) => e.date != null)
    .sort((a, b) => new Date(a.date ?? 0).getTime() - new Date(b.date ?? 0).getTime());
  const undated = all.filter((e) => e.date == null);
  return [...dated, ...undated];
}
